/**
 * Binding paths into normalised snapshots (PRESENTATION_SCHEMA.md §8):
 * `alias ( "." key | "[" index "]" | "[?" field "=" value "]" )*`. Keys are anything but
 * `.`, `[`, `]`, `|` and whitespace — other keys cannot be addressed by a template. A keyed
 * segment `[?id=123]` picks the first array item whose `field` equals `value` (stable when
 * items are reordered); value `[A-Za-z0-9_.:@-]{1,64}`.
 */
export interface KeySelector {
  field: string
  value: string
}
export type PathSegment = string | number | KeySelector

const KEY_SEGMENT_RE = /^[^.[\]\s|{}]+$/
export const KEY_FIELD_RE = /^[A-Za-z0-9_]+$/
export const KEY_VALUE_RE = /^[A-Za-z0-9_.:@-]{1,64}$/

/** True when an object key can be written as a `.key` path segment. */
export function isAddressableKey(key: string): boolean {
  return KEY_SEGMENT_RE.test(key)
}

export function isKeySelector(s: PathSegment): s is KeySelector {
  return typeof s === 'object' && s !== null
}

/** True when an item identifier can be written as `[?field=value]`. */
export function isKeyableValue(v: unknown): boolean {
  return (typeof v === 'string' && KEY_VALUE_RE.test(v)) || (typeof v === 'number' && Number.isInteger(v) && KEY_VALUE_RE.test(String(v)))
}

/** `menu`, `['items', 0, 'name']` → `menu.items[0].name`; `{field: 'id', value: '7'}` → `[?id=7]`. */
export function formatPath(alias: string, segments: readonly PathSegment[]): string {
  let out = alias
  for (const s of segments) out += typeof s === 'number' ? `[${s}]` : isKeySelector(s) ? `[?${s.field}=${s.value}]` : `.${s}`
  return out
}

/** Equality of two segment lists (keyed selectors by value). */
export function sameSegments(a: readonly PathSegment[] | null | undefined, b: readonly PathSegment[] | null | undefined): boolean {
  if (!a || !b || a.length !== b.length) return false
  return a.every((x, i) => {
    const y = b[i]!
    if (isKeySelector(x) || isKeySelector(y)) return isKeySelector(x) && isKeySelector(y) && x.field === y.field && x.value === y.value
    return x === y
  })
}

/** Parses `alias.key[0][?id=7].key` (whitespace-trimmed). Null on grammar errors. */
export function parsePath(src: string): { alias: string; segments: PathSegment[] } | null {
  const s = src.trim()
  const m = /^[a-z][a-z0-9_]{0,31}(?![a-z0-9_])/.exec(s)
  if (!m) return null
  const alias = m[0]
  const segments: PathSegment[] = []
  let i = alias.length
  while (i < s.length) {
    if (s[i] === '.') {
      const k = /^[^.[\]\s|{}]+/.exec(s.slice(i + 1))
      if (!k) return null
      segments.push(k[0])
      i += 1 + k[0].length
    } else if (s[i] === '[') {
      const k = /^\[(\d+)\]/.exec(s.slice(i))
      const q = k ? null : /^\[\?([A-Za-z0-9_]+)=([A-Za-z0-9_.:@-]{1,64})\]/.exec(s.slice(i))
      if (k) {
        segments.push(Number(k[1]))
        i += k[0].length
      } else if (q) {
        segments.push({ field: q[1]!, value: q[2]! })
        i += q[0].length
      } else return null
    } else return null
  }
  return { alias, segments }
}

/** Item identifiers compare as text (`123` matches `"123"`). */
function keyString(v: unknown): string | null {
  if (typeof v === 'string') return v
  if (typeof v === 'number' && Number.isFinite(v)) return String(v)
  return null
}

/** Value at `segments` below `root` (`found: false` when any step is missing). */
export function valueAt(root: unknown, segments: readonly PathSegment[]): { found: boolean; value: unknown } {
  let cur: unknown = root
  for (const seg of segments) {
    if (cur === null || typeof cur !== 'object') return { found: false, value: undefined }
    if (typeof seg === 'number') {
      if (!Array.isArray(cur) || seg >= cur.length) return { found: false, value: undefined }
      cur = cur[seg]
    } else if (isKeySelector(seg)) {
      if (!Array.isArray(cur)) return { found: false, value: undefined }
      const hit = cur.find((item) => item !== null && typeof item === 'object' && !Array.isArray(item) && Object.prototype.hasOwnProperty.call(item, seg.field) && keyString((item as Record<string, unknown>)[seg.field]) === seg.value)
      if (hit === undefined) return { found: false, value: undefined }
      cur = hit
    } else {
      if (Array.isArray(cur) || !Object.prototype.hasOwnProperty.call(cur, seg)) return { found: false, value: undefined }
      cur = (cur as Record<string, unknown>)[seg]
    }
  }
  return cur === undefined ? { found: false, value: undefined } : { found: true, value: cur }
}

export type JsonKind = 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null'

export function jsonKind(v: unknown): JsonKind {
  if (v === null || v === undefined) return 'null'
  if (Array.isArray(v)) return 'array'
  if (typeof v === 'object') return 'object'
  if (typeof v === 'string') return 'string'
  if (typeof v === 'number') return 'number'
  if (typeof v === 'boolean') return 'boolean'
  return 'null'
}

/** Only scalars render in text / QR templates (objects and arrays are unresolved, §8.2). */
export function isBindableValue(v: unknown): boolean {
  const k = jsonKind(v)
  return k === 'string' || k === 'number' || k === 'boolean'
}

export interface TreeEntry {
  /** Object key or array index (null for the root). */
  key: string | number | null
  segments: PathSegment[]
  kind: JsonKind
  value: unknown
  /** Number of children for objects / arrays. */
  size: number
  /** False when a key on the way cannot be written as a path segment. */
  addressable: boolean
}

function entry(key: string | number | null, segments: PathSegment[], value: unknown, addressable: boolean): TreeEntry {
  const kind = jsonKind(value)
  const size = kind === 'array' ? (value as unknown[]).length : kind === 'object' ? Object.keys(value as object).length : 0
  return { key, segments, kind, value, size, addressable }
}

export function rootEntry(value: unknown): TreeEntry {
  return entry(null, [], value, true)
}

/** Direct children of an object / array entry, capped at `limit` (default 200). */
export function childEntries(parent: TreeEntry, limit = 200): { entries: TreeEntry[]; hidden: number } {
  if (parent.kind === 'array') {
    const arr = parent.value as unknown[]
    const entries = arr.slice(0, limit).map((v, i) => entry(i, [...parent.segments, i], v, parent.addressable))
    return { entries, hidden: Math.max(0, arr.length - limit) }
  }
  if (parent.kind === 'object') {
    const keys = Object.keys(parent.value as object)
    const entries = keys.slice(0, limit).map((k) => entry(k, [...parent.segments, k], (parent.value as Record<string, unknown>)[k], parent.addressable && isAddressableKey(k)))
    return { entries, hidden: Math.max(0, keys.length - limit) }
  }
  return { entries: [], hidden: 0 }
}

/** Short one-line rendering of a value for the tree / pickers. */
export function previewValue(v: unknown, max = 80): string {
  const kind = jsonKind(v)
  let s: string
  switch (kind) {
    case 'string': {
      const str = v as string
      if (str.startsWith('data:')) return `"${str.slice(0, 24)}…" (${Math.round(str.length / 1024)} kB data URI)`
      s = JSON.stringify(str)
      break
    }
    case 'array':
      return `[${(v as unknown[]).length} item${(v as unknown[]).length === 1 ? '' : 's'}]`
    case 'object': {
      const n = Object.keys(v as object).length
      return `{${n} key${n === 1 ? '' : 's'}}`
    }
    default:
      s = String(v)
  }
  return s.length > max ? `${s.slice(0, max - 1)}…` : s
}

/**
 * Lists every addressable scalar leaf path (depth-first, arrays limited to `maxItems`
 * entries each) — used for path autocompletion.
 */
export function leafPaths(root: unknown, opts: { maxItems?: number; limit?: number } = {}): { segments: PathSegment[]; value: unknown }[] {
  const out: { segments: PathSegment[]; value: unknown }[] = []
  const maxItems = opts.maxItems ?? 3
  const limit = opts.limit ?? 500
  const walk = (v: unknown, segs: PathSegment[]) => {
    if (out.length >= limit) return
    const kind = jsonKind(v)
    if (kind === 'array') (v as unknown[]).slice(0, maxItems).forEach((x, i) => walk(x, [...segs, i]))
    else if (kind === 'object') {
      for (const [k, x] of Object.entries(v as object)) if (isAddressableKey(k)) walk(x, [...segs, k])
    } else if (segs.length && kind !== 'null') out.push({ segments: segs, value: v })
  }
  walk(root, [])
  return out
}
